/**
 * Lightweight, best-effort client-side error reporter.
 *
 * Because `SectionErrorBoundary` is a class component it cannot call hooks,
 * so we store a reference to the report function at module level once the
 * session is ready. The reporter is best-effort: failures are silently swallowed
 * and will never throw or cause recursion.
 */

type ReportFn = (payload: {
  code: string;
  message: string;
  route?: string | null;
  stack?: string | null;
  metadata?: Record<string, unknown> | null;
}) => void;

let _report: ReportFn | null = null;

/** Called once the authenticated session is available. */
export function setErrorReporter(fn: ReportFn | null): void {
  _report = fn;
}

/** Simple deduplication: skip the same (code+message) within the window. */
const _seen = new Set<string>();

export function reportClientError(payload: {
  code: string;
  message: string;
  route?: string | null;
  stack?: string | null;
  metadata?: Record<string, unknown> | null;
}): void {
  if (!_report) return;
  const key = `${payload.code}:${payload.message}`;
  if (_seen.has(key)) return;
  _seen.add(key);
  // Auto-clear dedup after 30 s so transient bursts are deduplicated but
  // recurring errors are still eventually captured.
  setTimeout(() => _seen.delete(key), 30_000);
  try {
    _report(payload);
  } catch {
    // swallow
  }
}
